import type { Getter, SortingFn } from "@tanstack/react-table";
import axios from "axios";
import moment from "moment";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ReactTooltip from "react-tooltip";
import styled, { useTheme } from "styled-components/macro";
import { match } from "ts-pattern";
import { useAuthContext } from "../../../../../../components/Auth";
import {
  ButtonWithConfirmDialog,
  TertiaryButtonMediumV2,
} from "../../../../../../components/Buttons/Buttons";
import { ConfirmDialog } from "../../../../../../components/ConfirmDialog/ConfirmDialog";
import { ErrorDialog } from "../../../../../../components/ErrorDialog/ErrorDialog";
import { IconGroup } from "../../../../../../components/DocumentCard/DocumentCard";
import {
  APIStatus,
  getBarentzApiStatusConfig,
} from "../../../../../../components/Status/Status";
import {
  CloneIcon,
  DeleteIcon,
  EditIcon,
  NonVisibleIcon,
  NotApplicableIcon,
  PlusIcon,
  ResubmitApprovalIcon,
  ErrorIcon,
  QuoteIcon,
} from "../../../../../../components/Icons/Icons";
import { useNotifications } from "../../../../../../components/Notifications/NotificationsContext";
import { Table } from "../../../../../../components/Table/Table";
import { TableStickyLoader } from "../../../../../../components/Table/TableStickyLoader";
import { TablePageLayout } from "../../../../../../components/TablePage/TablePageLayout";
import { FilterBy } from "../../../../../../components/FilterBy/FilterBy";
import { NumericFilterButton } from "../../../../../../components/Filters/NumericFilterInput";
import {
  toUrlKey,
  valueToDisplayStrings,
  matchesFilter,
  UNASSIGNED_FILTER_ID,
  getHighlightSegments,
  SearchHighlight,
  areSearchParamsEqual,
} from "../../../../../../util/collectionSearchUtils";
import type { OptionType, UUID } from "../../../../../../types/types";
import { NUMERIC_FILTER_OPERATORS } from "../../../../../../types/types";
import type {
  BarentzIntegrationMasterDataExportBatches,
  PIMProductPartial,
} from "../../../../../../types/types.PIM";
import { useRoutePath } from "../../../../../../util/Routing";
import { applyPartialPimProductAndRefresh } from "../../../../../../util/pimProductResponseMerge";
import {
  rowHover,
  TablePlaceholder,
  useStoreState,
} from "../../../../../../util/util";
import {
  getAxiosErrorStatusAndMessage,
  isDuplicateCollectionRowConflict,
} from "../../../../../../util/collectionConflictUtils";
import { ScrollTableWrapper } from "../../../../../SharedPages/ProductDetailPage/Assets/DocumentsOrDigitalMediaTable/DocumentsOrDigitalMediaTable";
import {
  GENERATED_UUID,
  generateUUID,
  getAttributeValue,
} from "./ProductCollectionSchema.util";
import type {
  FormValue,
  IRow,
  OnSubmitValue,
  ProductCollectionSchemaTableProps,
  MultiSelectRefType,
} from "./type";
import {
  DuplicateItemModal,
  DuplicateSKUModal,
  ResubmitConfirmationModal,
} from "../../../../../SharedPages/ProductDetailPage/Approvals/ConfirmationModals";
import type { ChipType } from "../../../../../../components/Chips/Chips";
import { useApprovalEligibilityContext } from "../../../../../SharedPages/ProductDetailPage/Approvals/ApprovalEligibilityContext";
import { CollectionSchemaSlideOut } from "./CollectionSchemaSlideOut";
import {
  CollectionAttributeDisplayValue,
  DocumentCollectionValue,
  getCollectionAttributeDisplayValue,
} from "../../../../../../util/collectionAttributeDisplayValue";
import { DocumentMoreModal } from "../../../../../../components/AttributeGroupFormGenerator/DocumentAttribute/DocumentMoreModal";
import { ObjectReferenceMoreModal } from "../../../../../../components/ObjectReference/ObjectReferenceMoreModal";
import { ProductCardsMoreModal } from "../../../../../../components/ProductCardsGrid/ProductCardsMoreModal";
import type {
  ObjectReferenceResult,
  ProductReference,
} from "../../../../../../types/types.PIM";
import { APIResyncButton } from "./APIResyncButton";
import { BarentzAPIErrorMessage } from "../../../../../../components/BarentzAPIErrorMessage/BarentzAPIErrorMessage";
import {
  OBJECT_REFERENCE_CONFIRMATION_PENDING,
  shouldShowObjectReferenceAttribute,
} from "../../../../../../components/ObjectReference/utils";
import { borderPrimitiveTokens } from "../../../../../../designTokens";
import { AccordionCard } from "../../../../../../components/AccordionCard/AccordionCard";
import { getProductTypePermissionSuffix } from "../../../../../SharedPages/ProductDetailPage/ProductOverview/product_overview.utils";
import { ChangeRequestFormModal } from "../../../../../../components/ChangeRequestForm/ChangeRequestModal";
import {
  ChangeRequestFormValues,
  submitChangeRequest,
} from "../../../../../../components/ChangeRequestForm/changerequest.util";
import {
  buildSkuAttributeValueLookup,
  getCellName,
  resolveSkuStatusLock,
} from "./approvalWorkflowHelpers";
import { useListFilterCarryOver } from "../../../../../../util/useListFilterCarryOver";

export type { MultiSelectRefType } from "./type";

const TableContainer = styled.div<{ canStickLastColumn?: boolean }>`
  margin-bottom: ${({ theme }) => theme.resolve("spacing_3xl")};
  & div[class*="TableWrapper"]:not([class*="ScrollTable"]) {
    overflow: visible;
    td:last-child:has(> #table-action-buttons) {
      position: ${({ canStickLastColumn }) =>
        canStickLastColumn ? "sticky" : "unset"};
      right: ${({ canStickLastColumn }) =>
        canStickLastColumn ? "0" : "unset"};
    }
    table:has(td:last-child > #table-action-buttons) th:last-child {
      position: ${({ canStickLastColumn }) =>
        canStickLastColumn ? "sticky" : "unset"};
      right: ${({ canStickLastColumn }) =>
        canStickLastColumn ? "0" : "unset"};
    }
  }
`;

export const NonVisibileContainer = styled.div`
  width: fit-content;
  height: fit-content;
`;

const RestrictedAccessHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.resolve("spacing_xs")};
  position: relative;

  ${NonVisibileContainer} {
    margin-left: ${({ theme }) => theme.resolve("spacing_xs")};
    display: inline-flex;
    align-items: center;
  }
`;

const InlineIconDisplay = styled.div`
  display: inline-flex;
  align-items: center;
  flex-wrap: nowrap;

  & > * {
    margin-right: ${({ theme }) => theme.resolve("spacing_xs")};
  }

  & > *:last-child {
    margin-right: 0;
  }
`;

interface IconButtonProps {
  $variant?: "destructive" | "standard";
  $borderWidth?: string;
}

const IconButton = styled.button<IconButtonProps>`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.resolve("spacing_xs")};
  border-radius: ${({ theme }) => theme.resolve("radius_buttonIcon_standard")};
  border: ${({ theme, $borderWidth }) =>
      $borderWidth ?? theme.resolve("border_none")}
    solid
    ${({ theme, $variant }) =>
      $variant === "destructive"
        ? theme.resolve("color_buttonIcon_border_destructive_enabled")
        : theme.resolve("color_buttonIcon_border_enabled")};
  background-color: ${({ theme, $variant }) =>
    $variant === "destructive"
      ? theme.resolve("color_buttonIcon_bg_destructive_enabled")
      : theme.resolve("color_buttonIcon_bg_enabled")};
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  outline: none;

  svg {
    width: 16px;
    height: 16px;
  }

  :hover,
  :focus {
    background-color: ${({ theme, $variant }) =>
      $variant === "destructive"
        ? theme.resolve("color_buttonIcon_bg_destructive_hover")
        : theme.resolve("color_buttonIcon_bg_hover")};
    border: ${({ theme }) => theme.resolve("border_md")} solid
      ${({ theme, $variant }) =>
        $variant === "destructive"
          ? theme.resolve("color_buttonIcon_border_destructive_hover")
          : theme.resolve("color_buttonIcon_border_hover")};

    svg {
      stroke: ${({ theme, $variant }) =>
        $variant === "destructive"
          ? theme.resolve("color_buttonIcon_icon_destructive_hover")
          : theme.resolve("color_buttonIcon_icon_hover")};
    }
  }

  :active {
    background-color: ${({ theme, $variant }) =>
      $variant === "destructive"
        ? theme.resolve("color_buttonIcon_bg_destructive_hover")
        : theme.resolve("color_buttonIcon_bg_hover")};
  }

  :disabled {
    cursor: not-allowed;
    opacity: 0.6;
    background-color: ${({ theme, $variant }) =>
      $variant === "destructive"
        ? theme.resolve("color_buttonIcon_bg_destructive_disabled")
        : theme.resolve("color_buttonIcon_bg_disabled")};
    border-color: ${({ theme, $variant }) =>
      $variant === "destructive"
        ? theme.resolve("color_buttonIcon_border_destructive_disabled")
        : theme.resolve("color_buttonIcon_border_disabled")};

    svg {
      stroke: ${({ theme, $variant }) =>
        $variant === "destructive"
          ? theme.resolve("color_buttonIcon_icon_destructive_disabled")
          : theme.resolve("color_buttonIcon_icon_disabled")};
    }
  }
`;

const DestructiveDeleteButton = styled(IconButton).attrs({
  $variant: "destructive" as const,
  $borderWidth: borderPrimitiveTokens.border_none,
})``;

const EditButton = styled(IconButton).attrs({
  $variant: "standard" as const,
})``;

const NoEditPlaceholder = (t: any) => (
  <TablePlaceholder message={t("No items to show.")} />
);

/**
 * Converts a FormValue to an IRow
 */
const convertFormValueToIRow = (
  formValue: FormValue,
  rowId: UUID,
  existingRow?: IRow
): IRow => {
  const convertedRow: IRow = {
    uuid: rowId,
    edit: existingRow?.edit ?? false,
    row_editable: existingRow?.row_editable ?? true,
    ...(existingRow?.id && { id: existingRow.id }),
    ...(existingRow?.oldUUID && { oldUUID: existingRow.oldUUID }),
  };

  Object.entries(formValue).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") {
      convertedRow[key] = null;
      return;
    }

    if (
      typeof value === "object" &&
      !Array.isArray(value) &&
      "value" in value &&
      "label" in value
    ) {
      if (!value.value) {
        convertedRow[key] = null;
        return;
      }
      const optionValue = value as OptionType<string | boolean>;
      convertedRow[key] = [{ name: `${optionValue.value}` }];
    } else if (Array.isArray(value)) {
      const updatedValue = value.map((v) => {
        if (v && typeof v === "object" && "value" in v && "label" in v) {
          return { name: `${v.value}` };
        }
        if (typeof v === "string") {
          return { name: v };
        }
        return v;
      });
      convertedRow[key] = updatedValue;
    } else {
      convertedRow[key] = value;
    }
  });

  return convertedRow;
};

export const ProductCollectionSchemaTable = ({
  collectionSchema,
  barentzApiBatches,
  onSubmitForm,
  onDeleteRow,
  title,
  isPortfolio = false,
  productStatus,
  isEditable,
  isLockedForEdit,
  showMandatoryAsterisk,
  fetchProductData,
  productID,
  productOwner,
  pimProductType,
  isProductEntry = false,
}: ProductCollectionSchemaTableProps) => {
  const [tableData, setTableData] = useState<IRow[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmittingApplicability, setIsSubmittingApplicability] =
    useState(false);
  const [rowInEdit, setRowInEdit] = useState<UUID | null>(null);
  const [changeRequestRow, setChangeRequestRow] =
    useState<UUID | undefined>(undefined);
  const [showSlideOut, setShowSlideOut] = useState(false);
  const [showChangeRequestSlideOut, setshowChangeRequestSlideOut] =
    useState(false);
  const [isNewRow, setIsNewRow] = useState(false);
  const [isClone, setIsClone] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showDuplicateRowError, setShowDuplicateRowError] = useState(false);
  const [pendingAction, setPendingAction] =
    useState<{
      columnId: UUID;
      newStatus: boolean;
    } | null>(null);
  const [clonedOriginalValue, setClonedOriginalValue] = useState<
    Record<UUID, IRow | string>
  >({});
  const [showDuplicateSkuModal, setShowDuplicateSkuModal] = useState(false);
  const [matchingSkuId, setMatchingSkuId] = useState<string | null>(null);
  const [resubmitSkuId, setResubmitSkuId] = useState<string | null>(null);
  const [showResubmitModal, setShowResubmitModal] = useState(false);
  const [showAPIStatusModal, setShowAPIStatusModal] = useState(false);
  const [selectedAPIStatusBatch, setSelectedAPIStatusBatch] =
    useState<BarentzIntegrationMasterDataExportBatches | null>(null);
  const [skuIdDisplayName, setSkuIdDisplayName] = useState<string>("SKU ID");
  const [documentMoreDocs, setDocumentMoreDocs] =
    useState<{
      docs: DocumentCollectionValue[];
      attributeName: string;
    } | null>(null);
  const [objectReferenceMore, setObjectReferenceMore] =
    useState<{
      references: ObjectReferenceResult[];
      attributeName: string;
      targetObjectType?: string;
    } | null>(null);
  const [productReferenceMore, setProductReferenceMore] =
    useState<{
      references: ProductReference[];
      attributeName: string;
    } | null>(null);

  const { t } = useTranslation();
  const history = useHistory();
  const { search } = useLocation();

  // URL namespace per collection so multiple tables on the same page don't clash.
  const urlPrefix = useMemo(
    () => toUrlKey(collectionSchema.name),
    [collectionSchema.name]
  );

  const [searchQuery, setSearchQuery] = useState<string>(() => {
    const params = new URLSearchParams(search);
    return params.get(`${urlPrefix}_q`) ?? "";
  });
  const [activeFilters, setActiveFilters] = useState<
    Record<string, ChipType[]>
  >(() => {
    const params = new URLSearchParams(search);
    const result: Record<string, ChipType[]> = {};
    const filterPrefix = `${urlPrefix}_f_`;
    const nameSlugToId: Record<string, string> = {};
    collectionSchema.columns.forEach((col) => {
      if (!col.is_filter_eligible) return;
      nameSlugToId[toUrlKey(col.name)] = col.id;
    });

    // Collection URL filters.
    params.forEach((value, key) => {
      if (!key.startsWith(filterPrefix)) return;
      const colId = nameSlugToId[key.slice(filterPrefix.length)];
      if (!colId) return;
      const chips = value
        .split(",")
        .filter(Boolean)
        .map((v) => {
          const decoded = decodeURIComponent(v);
          // Restore the Unassigned sentinel with its special ID so matching works.
          if (decoded === UNASSIGNED_FILTER_ID) {
            return { name: t("Unassigned"), id: UNASSIGNED_FILTER_ID };
          }
          return { name: decoded, id: decoded };
        });
      if (chips.length > 0) result[colId] = chips;
    });
    return result;
  });

  const translateDisplayName = useCallback(
    (displayName: string) => t([displayName]),
    [t]
  );
  const { markDismissedIfCarriedOver } = useListFilterCarryOver({
    domain: "product",
    scopeId: productID,
    collectionId: collectionSchema.id,
    listSearchStorageKey: "products_search",
    columns: collectionSchema.columns,
    translateDisplayName,
    setActiveFilters,
  });

  // Keep a ref so the URL-sync effect can read the latest column definitions
  // without depending on them — SWR revalidations return a new array reference
  // which would otherwise re-run the effect mid-keystroke.
  const collectionColumnsRef = useRef(collectionSchema.columns);
  collectionColumnsRef.current = collectionSchema.columns;

  // Sync search + filter state to URL whenever they change.
  useEffect(() => {
    const params = new URLSearchParams(search);
    const filterPrefix = `${urlPrefix}_f_`;

    if (searchQuery) {
      params.set(`${urlPrefix}_q`, searchQuery);
    } else {
      params.delete(`${urlPrefix}_q`);
    }

    const toDelete: string[] = [];
    params.forEach((_, key) => {
      if (key.startsWith(filterPrefix)) toDelete.push(key);
    });
    toDelete.forEach((k) => params.delete(k));

    Object.entries(activeFilters).forEach(([colId, chips]) => {
      if (chips.length > 0) {
        const col = collectionColumnsRef.current.find((c) => c.id === colId);
        const colKey = col ? toUrlKey(col.name) : colId;
        params.set(
          `${filterPrefix}${colKey}`,
          chips
            .map((c) =>
              c.id === UNASSIGNED_FILTER_ID
                ? UNASSIGNED_FILTER_ID
                : encodeURIComponent(c.name)
            )
            .join(",")
        );
      }
    });
    if (!areSearchParamsEqual(params, search)) {
      history.replace({ search: params.toString() });
    }
  }, [searchQuery, activeFilters, urlPrefix, history, search]);

  const { triggerUpdate, uiRules } = useApprovalEligibilityContext();
  const {
    storefront_id,
    tenant_id,
    storefront_metadata: {
      enable_generic_sku,
      generic_sku_packaging_type_name,
      enable_reference_entities,
      enable_approval_workflow,
      enable_barentz_api_integration,
      object_reference_enabled,
      enable_change_requests,
    },
  } = useStoreState();
  const productTypePermissionSuffix =
    getProductTypePermissionSuffix(pimProductType);
  const { storePath, adminPath } = useRoutePath();
  const theme = useTheme();
  const collectionTableRef = useRef<HTMLDivElement>(null);
  const multiSelectRefs = useRef<{
    [key: string]: MultiSelectRefType;
  }>({});

  const { notifySuccess, notifyError } = useNotifications();
  const { hasPermission, roleIsSellerAdmin, user } = useAuthContext();

  const columns = useMemo(() => {
    const visibleColumns = isProductEntry
      ? collectionSchema?.columns
      : !isPortfolio
      ? collectionSchema?.columns.filter(({ is_visible }) => is_visible)
      : collectionSchema?.columns.filter(
          ({ is_restricted, is_visible }) => !is_restricted && is_visible
        );

    return visibleColumns?.filter((col) =>
      shouldShowObjectReferenceAttribute({
        inputType: col.input_type,
        objectReferenceEnabled: object_reference_enabled,
      })
    );
  }, [
    collectionSchema?.columns,
    isPortfolio,
    isProductEntry,
    object_reference_enabled,
  ]);

  const skuSourceColId = useMemo(
    () =>
      columns?.find(
        (col) => col.object_type === "source" || col.object_type === "region"
      )?.id,
    [columns]
  );

  const skuStatusColId = useMemo(
    () => columns?.find((col) => col.object_type === "sku_status")?.id,
    [columns]
  );

  const skuIdColId = useMemo(
    () => columns?.find((col) => col.object_type === "sku_id")?.id,
    [columns]
  );

  const packagingTypeColId = useMemo(
    () => columns?.find((col) => col.object_type === "packaging_type")?.id,
    [columns]
  );

  const shouldShowBarentzApiStatusFeatures = useMemo(
    () =>
      !isProductEntry &&
      (isEditable ||
        (!isEditable && isLockedForEdit && hasPermission("modify_products"))) &&
      enable_approval_workflow &&
      enable_barentz_api_integration &&
      collectionSchema.object_type === "skus",
    [
      isProductEntry,
      isEditable,
      isLockedForEdit,
      hasPermission,
      enable_approval_workflow,
      enable_barentz_api_integration,
      collectionSchema.object_type,
    ]
  );

  const canStickLastColumn = useMemo(() => {
    if (isPortfolio || productStatus === "archived" || !isEditable) {
      return false;
    }

    const hasRowEditable = tableData.some(
      (row) => row.row_editable && !row.uuid.includes(GENERATED_UUID)
    );
    if (!hasRowEditable) {
      return false;
    }

    return (
      hasPermission("modify_products") ||
      hasPermission(`modify_${productTypePermissionSuffix}`) ||
      hasPermission("delete_products") ||
      hasPermission(`delete_${productTypePermissionSuffix}`)
    );
  }, [
    hasPermission,
    isEditable,
    isPortfolio,
    productStatus,
    tableData,
    productTypePermissionSuffix,
  ]);

  const onSubmit = async (value: OnSubmitValue) => {
    const row_in_edit = tableData.find((row) => row.uuid === value.row_id);
    const isApprovalSKUCollection =
      enable_approval_workflow &&
      collectionSchema.display_name?.toLowerCase().includes("sku");
    const changeResult = hasValuesChanged(
      convertFormValueToIRow(value, value.row_id),
      value.row_id
    );
    const isBlockedDuplicate =
      row_in_edit &&
      (isApprovalSKUCollection ||
        row_in_edit.uuid === clonedOriginalValue["clone_uuid"]) &&
      changeResult !== true;

    if (isBlockedDuplicate) {
      const skuIdColumnId = skuIdColId;
      let matchingSkuId: string | null = null;
      if (typeof changeResult === "object" && "duplicateRow" in changeResult) {
        matchingSkuId =
          skuIdColumnId && changeResult.duplicateRow[skuIdColumnId]
            ? (changeResult.duplicateRow[skuIdColumnId] as string)
            : null;
      } else {
        const originalRowUuid = Object.keys(clonedOriginalValue)[0] as UUID;
        const originalRow = tableData.find(
          (row) => row.uuid === originalRowUuid
        );
        matchingSkuId =
          originalRow && skuIdColumnId
            ? (originalRow[skuIdColumnId] as string)
            : null;
      }
      setMatchingSkuId(matchingSkuId);
      setShowDuplicateSkuModal(true);
      return;
    }
    setIsSubmitting(true);
    try {
      const msg = await onSubmitForm(value);
      if (msg === OBJECT_REFERENCE_CONFIRMATION_PENDING) {
        closeSlideOut();
        return;
      }
      if (
        msg === t("No changes to save") &&
        value.row_id.includes(GENERATED_UUID)
      ) {
        setTableData((prev) => prev.filter((row) => row.uuid !== value.row_id));
      }
      notifySuccess(msg ?? t("Your changes have been saved successfully"));
      closeSlideOut();
    } catch (error) {
      const { message } = getAxiosErrorStatusAndMessage(error);
      if (isDuplicateCollectionRowConflict(error)) {
        setShowDuplicateRowError(true);
      } else {
        notifyError(
          message ?? t("There was an error modifying this collection"),
          {
            error,
          }
        );
      }
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  };

  const closeSlideOut = () => {
    // Remove temporary row if it was a new row that wasn't saved
    if (isNewRow && rowInEdit) {
      setTableData((prev) =>
        prev.filter((row) => !row.uuid.includes(GENERATED_UUID))
      );
    }
    setShowSlideOut(false);
    setRowInEdit(null);
    setIsNewRow(false);
    setIsClone(false);
    setClonedOriginalValue({});
  };

  const addItem = () => {
    const uuid = generateUUID(tableData);
    multiSelectRefs.current = {};
    setTableData((prev) => [
      ...prev,
      {
        edit: true,
        row_editable: true,
        uuid,
      },
    ]);
    setRowInEdit(uuid);
    setIsNewRow(true);
    setIsClone(false);
    setShowSlideOut(true);
  };

  const rowHoverProcessor = useCallback(
    (data: any) => {
      const col_id = data.column.id as string;
      const col_type = columns.find((col) => col.id === col_id)?.input_type;
      const value = data.getValue();
      if (
        col_type === "single_select" ||
        (col_type === "multi_select" &&
          Array.isArray(value) &&
          value.length === 1)
      ) {
        return (value?.[0]?.name as string) ?? null;
      }
    },
    [columns]
  );

  const hasValuesChanged = useCallback(
    (
      currentValues: IRow,
      currentRowUuid?: UUID
    ): boolean | { duplicateRow: IRow } => {
      const valuesAreEqual = (val1: any, val2: any): boolean => {
        const objectValuesAreEqual = (v1: any, v2: any): boolean => {
          // Documents: table holds asset objects, form submits { asset_id, value_id }.
          return v1?.asset_id != null
            ? v1.asset_id === v2?.asset_id
            : v1?.value != null && v1?.label != null
            ? v1.value === v2?.value && v1.label === v2?.label
            : v1?.name != null
            ? v1.name === v2?.name
            : false;
        };
        const isBlank = (v: any) => !v || (Array.isArray(v) && v.length === 0);
        if (val1 === val2) return true;
        if (isBlank(val1) || isBlank(val2)) {
          return isBlank(val1) && isBlank(val2);
        }
        if (Array.isArray(val1) && Array.isArray(val2)) {
          if (val1.length !== val2.length) return false;
          return val1.every((v1, i) => {
            const v2 = val2[i];
            if (typeof v1 === "object" && typeof v2 === "object") {
              return objectValuesAreEqual(v1, v2);
            }
            return v1 === v2;
          });
        }
        if (typeof val1 === "object" && typeof val2 === "object") {
          return objectValuesAreEqual(val1, val2);
        }
        return false;
      };

      const shouldSkipColumn = (colId: string) =>
        colId === skuIdColId || colId === skuStatusColId;

      const rowMatchesOther = (rowA: IRow, rowB: IRow): boolean => {
        for (const col of columns ?? []) {
          if (shouldSkipColumn(col.id)) continue;
          if (!valuesAreEqual(rowA[col.id], rowB[col.id])) return false;
        }
        return true;
      };

      const originalValues = Object.values(clonedOriginalValue)[0] as
        | IRow
        | undefined;

      if (originalValues) {
        // Cloned row
        for (const col of columns ?? []) {
          if (shouldSkipColumn(col.id)) continue;
          if (!valuesAreEqual(originalValues[col.id], currentValues[col.id])) {
            return true;
          }
        }
        return false;
      }

      // Add/edit row
      const isApprovalSKUCollection =
        enable_approval_workflow &&
        collectionSchema.display_name?.toLowerCase().includes("sku");
      if (!isApprovalSKUCollection) {
        return true;
      }

      const otherRows = tableData.filter(
        (row) => row.uuid !== currentRowUuid && !row.edit
      );
      for (const other of otherRows) {
        if (rowMatchesOther(currentValues, other)) {
          return { duplicateRow: other }; // Duplicate row
        }
      }
      return true;
    },
    [
      clonedOriginalValue,
      columns,
      enable_approval_workflow,
      collectionSchema.display_name,
      skuIdColId,
      skuStatusColId,
      tableData,
    ]
  );

  const handleResubmitApproval = useCallback((skuId: string | null) => {
    setShowResubmitModal(true);
    setResubmitSkuId(skuId);
  }, []);

  const cloneItem = useCallback(
    async (sourceRow: IRow) => {
      const updated_source_row = { ...sourceRow };
      const clone_uuid = generateUUID(tableData);
      multiSelectRefs.current = {};
      const sku_id = columns?.find((col) => col.object_type === "sku_id")?.id;
      if (sku_id) {
        updated_source_row[sku_id] = null;
      }
      const { edit, id, row_editable, uuid, oldUUID, ...rest } =
        updated_source_row;
      setClonedOriginalValue({
        [uuid]: rest as IRow,
        clone_uuid,
      });
      setTableData((prev) => [
        ...prev,
        {
          ...rest,
          id: `row-${clone_uuid}`,
          edit: true,
          row_editable: true,
          uuid: clone_uuid,
          cloned_original_uuid: uuid,
        },
      ]);
      setRowInEdit(clone_uuid);
      setIsNewRow(true);
      setIsClone(true);
      setShowSlideOut(true);
    },
    [columns, tableData]
  );

  const filterableColumns = useMemo(
    () => (columns ?? []).filter((col) => col.is_filter_eligible),
    [columns]
  );

  const columnFilterOptions = useMemo(() => {
    // Only operate on saved rows (exclude temporary generated-UUID rows).
    const savedRows = tableData.filter(
      (row) => !row.uuid.includes(GENERATED_UUID)
    );

    const query = searchQuery.trim().toLowerCase();
    const searchFiltered = query
      ? savedRows.filter((row) =>
          (columns ?? []).some((col) => {
            const value = row[col.id as keyof IRow];
            return valueToDisplayStrings(col, value).some((s) =>
              s.toLowerCase().includes(query)
            );
          })
        )
      : savedRows;

    const result: Record<string, ChipType[]> = {};
    filterableColumns.forEach((col) => {
      let dataForCol = searchFiltered;
      Object.entries(activeFilters).forEach(([colId, chips]) => {
        if (colId === col.id || chips.length === 0) return;
        const siblingCol = (columns ?? []).find((c) => c.id === colId);
        if (!siblingCol) return;
        dataForCol = dataForCol.filter((row) =>
          matchesFilter(siblingCol, row[colId as keyof IRow], chips)
        );
      });

      const seen = new Set<string>();
      const options: ChipType[] = [];
      let hasEmptyRows = false;

      dataForCol.forEach((row) => {
        const cellValue = row[col.id as keyof IRow];
        const displayStrings = valueToDisplayStrings(col, cellValue);
        if (displayStrings.length === 0) {
          hasEmptyRows = true;
          return;
        }
        displayStrings.forEach((str) => {
          const trimmed = str.trim();
          if (trimmed && !seen.has(trimmed)) {
            seen.add(trimmed);
            options.push({ name: trimmed, id: trimmed });
          }
        });
      });

      const filteredOptions =
        col.input_type === "date"
          ? options.filter((o) => !/^\d{4}-\d{2}-\d{2}/.test(o.name))
          : options;

      if (hasEmptyRows) {
        filteredOptions.push({
          name: t("Unassigned"),
          id: UNASSIGNED_FILTER_ID,
        });
      }

      if (filteredOptions.length > 0) {
        result[col.id] = filteredOptions;
      }
    });
    return result;
  }, [tableData, columns, filterableColumns, searchQuery, activeFilters, t]);

  // Only prune the Unassigned sentinel when it is no longer a valid option.
  // Never drop value chips based on `columnFilterOptions` — that list only
  // reflects values present in *this* collection's current rows, but
  // products-list carry-over often references values absent from this
  // product's rows, and wiping them clears the URL and looks like carry-over
  // never ran (see useListFilterCarryOver.ts).
  useEffect(() => {
    if (tableData.length > 0) {
      setActiveFilters((prev) => {
        let changed = false;
        const next: Record<string, ChipType[]> = {};

        Object.entries(prev).forEach(([colId, chips]) => {
          const hasUnassignedChip = chips.some(
            (chip) => chip.id === UNASSIGNED_FILTER_ID
          );
          if (!hasUnassignedChip) {
            next[colId] = chips;
            return;
          }
          const unassignedStillValid = !!columnFilterOptions[colId]?.some(
            (o) => o.id === UNASSIGNED_FILTER_ID
          );
          if (unassignedStillValid) {
            next[colId] = chips;
            return;
          }
          changed = true;
          next[colId] = chips.filter(
            (chip) => chip.id !== UNASSIGNED_FILTER_ID
          );
        });

        return changed ? next : prev;
      });
    }
  }, [columnFilterOptions, tableData]);

  const handleFilterChange = useCallback(
    ({ values, filter }: { values: ChipType[]; filter: string }) => {
      setActiveFilters((prev) => {
        const next = { ...prev, [filter]: values };
        const hasAny = Object.values(next).some((chips) => chips.length > 0);
        if (!hasAny) {
          markDismissedIfCarriedOver();
        }
        return next;
      });
    },
    [markDismissedIfCarriedOver]
  );

  const handleClearAllFilters = useCallback(() => {
    markDismissedIfCarriedOver();
    setActiveFilters({});
  }, [markDismissedIfCarriedOver]);

  // The displayed table data: exclude temp rows, then apply search + filters.
  const filteredTableData = useMemo(() => {
    let data = tableData.filter(
      (row) => !row.uuid.includes(GENERATED_UUID) || !isNewRow
    );

    const query = searchQuery.trim().toLowerCase();
    if (query) {
      data = data.filter((row) =>
        (columns ?? []).some((col) => {
          const value = row[col.id as keyof IRow];
          return valueToDisplayStrings(col, value).some((s) =>
            s.toLowerCase().includes(query)
          );
        })
      );
    }

    Object.entries(activeFilters).forEach(([colId, chips]) => {
      if (chips.length === 0) return;
      const col = (columns ?? []).find((c) => c.id === colId);
      if (!col) return;
      data = data.filter((row) =>
        matchesFilter(col, row[colId as keyof IRow], chips)
      );
    });

    return data;
  }, [tableData, isNewRow, searchQuery, columns, activeFilters]);

  const handleConfirmApplicability = async () => {
    if (pendingAction) {
      setShowConfirmDialog(false);
      try {
        setIsSubmittingApplicability(true);
        const { data: partial } = await axios.patch<PIMProductPartial>(
          `/v2/storefronts/${storefront_id}/pim/products/${productID}/collections/${collectionSchema.id}/columns/${pendingAction.columnId}`,
          { is_not_applicable: pendingAction.newStatus }
        );
        await applyPartialPimProductAndRefresh(fetchProductData, partial);
        notifySuccess(
          t(
            pendingAction.newStatus
              ? "Column marked as not applicable"
              : "Column marked as applicable"
          )
        );
      } catch (error) {
        notifyError(
          t(
            pendingAction.newStatus
              ? "Failed to mark column as not applicable"
              : "Failed to mark column as applicable"
          ),
          { error }
        );
      } finally {
        setIsSubmittingApplicability(false);
        setPendingAction(null);
      }
    }
  };

  const tableColumns = useMemo(() => {
    const handleEditRow = async (id: UUID) => {
      setRowInEdit(id);
      setIsNewRow(false);
      setIsClone(false);
      setShowSlideOut(true);
      multiSelectRefs.current = {};
    };

    const handleChangeRequesrRow = async (id: UUID) => {
      setChangeRequestRow(id);
      setshowChangeRequestSlideOut(true);
    };

    const handleDeleteRow = async (id: UUID) => {
      try {
        setIsSubmitting(true);
        await onDeleteRow(id);
        setTableData((prev) =>
          prev.reduce<IRow[]>((acc, cur) => {
            if (cur.uuid !== id) {
              acc.push(cur);
            }
            return acc;
          }, [])
        );
        notifySuccess(t("Item deleted successfully."));
      } catch (error) {
        const { message } = getAxiosErrorStatusAndMessage(error);
        if (isDuplicateCollectionRowConflict(error)) {
          setShowDuplicateRowError(true);
        } else {
          notifyError(
            message ?? t("Could not delete row. Please try again later"),
            {
              error,
            }
          );
        }
      } finally {
        setIsSubmitting(false);
      }
    };

    const sortingFn: SortingFn<IRow> = (rowA, rowB, _columnId) => {
      const valueA =
        rowA.getValue(_columnId) === undefined
          ? 0
          : (rowA.getValue(_columnId) as { name: string }[])[0]?.name ??
            (rowA.getValue(_columnId) as { display_text: string; url: string })
              .display_text ??
            rowA.getValue(_columnId);
      const valueB =
        rowB.getValue(_columnId) === undefined
          ? 0
          : (rowB.getValue(_columnId) as { name: string }[])[0]?.name ??
            (rowB.getValue(_columnId) as { display_text: string; url: string })
              .display_text ??
            rowB.getValue(_columnId);
      if (valueA < valueB) {
        return -1;
      }
      if (valueA > valueB) {
        return 1;
      }
      return 0;
    };

    const getResubmitApprovalButton = (
      original: IRow,
      is_disabled: boolean
    ) => {
      function getFirstArrayItem<T = unknown>(
        obj: Record<string, any>,
        key?: string
      ): T | null {
        if (!key) return null;
        const arr = obj[key];
        return Array.isArray(arr) && arr.length > 0 ? arr[0] : null;
      }
      const skuSource = getFirstArrayItem<ChipType>(original, skuSourceColId);
      const skuStatus = getFirstArrayItem<ChipType>(original, skuStatusColId);
      const attributeValues = buildSkuAttributeValueLookup({
        columns,
        row: original as Record<string, unknown>,
      });
      if (skuSource) {
        attributeValues.region = getCellName(skuSource);
      }

      const is_resubmit_approval_icon_visible =
        enable_approval_workflow &&
        resolveSkuStatusLock({
          collectionName: collectionSchema.object_type ?? collectionSchema.name,
          attributeValues,
          uiRules,
        }).locked &&
        skuStatus?.name?.toLowerCase().trim() === "rejected";

      const sku_col_id = columns?.find(
        (col) => col.object_type === "sku_id"
      )?.id;
      const skuId = sku_col_id ? (original[sku_col_id] as string) : null;
      if (
        is_resubmit_approval_icon_visible &&
        hasPermission("modify_products") &&
        isEditable &&
        original.row_editable
      ) {
        return (
          <EditButton
            disabled={is_disabled}
            onClick={() => handleResubmitApproval(skuId)}
            title={t("Resubmit Approval")}
            type="button"
          >
            <ResubmitApprovalIcon />
          </EditButton>
        );
      }
    };

    const getCloneButton = (original: IRow, is_disabled: boolean) => {
      if (
        collectionSchema.object_type === "skus" &&
        hasPermission("modify_products") &&
        isEditable &&
        original.row_editable
      ) {
        return (
          <EditButton
            disabled={is_disabled}
            onClick={() => cloneItem(original)}
            title={t("Clone")}
            type="button"
          >
            <CloneIcon />
          </EditButton>
        );
      }
    };

    const getDeleteButton = (original: IRow, is_disabled: boolean) => {
      if (
        hasPermission("delete_products") &&
        hasPermission(`delete_${productTypePermissionSuffix}`) &&
        isEditable &&
        original.row_editable
      ) {
        return (
          <ButtonWithConfirmDialog
            Button={(props) => (
              <DestructiveDeleteButton {...props} type="button">
                <DeleteIcon />
              </DestructiveDeleteButton>
            )}
            disabled={is_disabled}
            testid={"remove-row-from-collection"}
            handleConfirm={() => handleDeleteRow(original.uuid)}
            confirmMessage={t("Are you sure you want to remove this item?")}
            title={t("Remove Item")}
            confirmBtnLabel={t("Remove")}
            confirmBtnType="destructive"
            useV2Buttons={true}
          />
        );
      }
    };

    const getEditButton = (original: IRow, is_disabled: boolean) => {
      if (
        hasPermission("modify_products") &&
        hasPermission(`modify_${productTypePermissionSuffix}`) &&
        isEditable &&
        original.row_editable
      ) {
        return (
          <EditButton
            disabled={is_disabled}
            onClick={() => handleEditRow(original.uuid)}
            title={t("Edit")}
            type="button"
          >
            <EditIcon />
          </EditButton>
        );
      } else if (
        enable_change_requests &&
        productStatus !== "archived" &&
        !hasPermission(`modify_${productTypePermissionSuffix}`)
      ) {
        return (
          <EditButton
            disabled={is_disabled}
            onClick={() => handleChangeRequesrRow(original.uuid)}
            title={t("Edit")}
            type="button"
          >
            <QuoteIcon />
          </EditButton>
        );
      }
    };

    const baseColumns =
      columns?.reduce<
        {
          header: () => string | JSX.Element;
          accessorKey: string;
          [prop: string]: any;
        }[]
      >((acc, col, index) => {
        if (col.input_type !== "multiline_entry") {
          const contextMenuItems = [
            ...(col.is_editable === false || isSubmittingApplicability
              ? []
              : [
                  {
                    key: "toggle-applicability",
                    label: col.is_not_applicable
                      ? t("Mark as Applicable")
                      : t("Mark as Not Applicable"),
                    value: col.is_not_applicable
                      ? "mark_applicable"
                      : "mark_na",
                    icon: (
                      <NotApplicableIcon
                        width={16}
                        height={16}
                        fill={
                          col.is_not_applicable
                            ? theme.errorColor
                            : theme.secondaryIconColor
                        }
                      />
                    ),
                  },
                ]),
          ];

          const contextMenu = {
            items: contextMenuItems,
            onSelect: async (value: string) => {
              const newStatus = value === "mark_na" ? true : false;
              setPendingAction({
                columnId: col.id,
                newStatus,
              });
              setShowConfirmDialog(true);
            },
          };

          acc.push({
            header: () => {
              const displayName = col.display_name
                ? t([col.display_name])
                : col.name;
              const mandatoryIndicator =
                !isPortfolio && col.is_mandatory && showMandatoryAsterisk
                  ? " *"
                  : "";

              return match({
                isRestricted: col.is_restricted,
                isNotApplicable: col.is_not_applicable,
              })
                .with({ isRestricted: true }, () => (
                  <RestrictedAccessHeader>
                    <InlineIconDisplay>
                      <span>{displayName}</span>
                      {mandatoryIndicator}
                      <NonVisibileContainer
                        data-tip={t(
                          "This attribute is only visible for internal users."
                        )}
                        data-for="non-visible-tip"
                      >
                        <NonVisibleIcon
                          width={15.7}
                          height={15.7}
                          fill={theme.secondaryIconColor}
                        />
                      </NonVisibileContainer>
                      {col.is_not_applicable && (
                        <NonVisibileContainer
                          data-tip={t(
                            "This attribute is marked as not applicable"
                          )}
                          data-for="not-applicable-tip"
                        >
                          <NotApplicableIcon
                            width={15.7}
                            height={15.7}
                            fill={theme.errorColor}
                          />
                        </NonVisibileContainer>
                      )}
                    </InlineIconDisplay>
                  </RestrictedAccessHeader>
                ))
                .with({ isNotApplicable: true }, () => (
                  <InlineIconDisplay>
                    <span>{displayName}</span>
                    {mandatoryIndicator}
                    <NonVisibileContainer
                      data-tip={t("This attribute is marked as not applicable")}
                      data-for="not-applicable-tip"
                    >
                      <NotApplicableIcon
                        width={15.7}
                        height={15.7}
                        fill={theme.errorColor}
                      />
                    </NonVisibileContainer>
                  </InlineIconDisplay>
                ))
                .otherwise(() => (
                  <InlineIconDisplay>
                    <span>{displayName}</span>
                    {mandatoryIndicator}
                    {col.is_not_applicable && (
                      <NonVisibileContainer
                        data-tip={t(
                          "This attribute is marked as not applicable"
                        )}
                        data-for="not-applicable-tip"
                      >
                        <NotApplicableIcon
                          width={15.7}
                          height={15.7}
                          fill={theme.errorColor}
                        />
                      </NonVisibileContainer>
                    )}
                  </InlineIconDisplay>
                ));
            },
            accessorKey: col.id,
            meta:
              !col.is_mandatory &&
              !isPortfolio &&
              hasPermission("modify_products") &&
              hasPermission(`modify_${productTypePermissionSuffix}`) &&
              isEditable
                ? {
                    contextMenu,
                  }
                : undefined,
            overflow: "hidden",
            sortingFn: sortingFn,
            cell: ({
              row: { original },
              getValue,
            }: {
              row: { original: IRow };
              getValue: Getter<string | null | boolean | { name: string }[]>;
            }) => {
              const rawValue = getValue() as CollectionAttributeDisplayValue;
              const query = searchQuery.trim().toLowerCase();
              const highlighted =
                !!query &&
                valueToDisplayStrings(col, rawValue).some((s) =>
                  s.toLowerCase().includes(query)
                );
              const displayContent = getCollectionAttributeDisplayValue(
                col,
                rawValue,
                {
                  isLoading: isSubmitting && original.uuid === rowInEdit,
                  theme,
                  t,
                  user,
                  roleIsSellerAdmin,
                  hasPermission,
                  storePath,
                  adminPath,
                  onDocumentMoreClick: (docs, attributeName) =>
                    setDocumentMoreDocs({ docs, attributeName }),
                  onObjectReferenceMoreClick: (
                    references,
                    attributeName,
                    targetObjectType
                  ) =>
                    setObjectReferenceMore({
                      references,
                      attributeName,
                      targetObjectType,
                    }),
                  onProductReferenceMoreClick: (references, attributeName) =>
                    setProductReferenceMore({ references, attributeName }),
                }
              );
              // For plain string/number values, bold the matching portion inline.
              // For complex React elements (links, badges, etc.) the yellow
              // background via $active is the visual indicator.
              const isPlainText =
                typeof displayContent === "string" ||
                typeof displayContent === "number";
              return (
                <SearchHighlight
                  $active={highlighted}
                  className="search-highlight"
                >
                  {highlighted && isPlainText ? (
                    <span>
                      {getHighlightSegments(
                        String(displayContent),
                        searchQuery
                      ).map((segment, index) =>
                        segment.matched ? (
                          <b key={index}>{segment.text}</b>
                        ) : (
                          segment.text
                        )
                      )}
                    </span>
                  ) : (
                    displayContent
                  )}
                </SearchHighlight>
              );
            },
          });
        }
        if (
          index === columns.length - 1 &&
          !isPortfolio &&
          productStatus !== "archived" &&
          columns.some((col) => col.is_editable !== false)
        ) {
          acc.push({
            header: () => "",
            accessorKey: " ",
            enableSorting: false,
            disableResize: true,
            cell: ({ row: { original } }: { row: { original: IRow } }) => {
              const is_disabled =
                isSubmitting || isSubmittingApplicability || showSlideOut;

              // Don't show action buttons for temporary new rows
              if (original.uuid.includes(GENERATED_UUID)) {
                return null;
              }

              return (
                <IconGroup
                  id="table-action-buttons"
                  style={{
                    justifyContent: "flex-end",
                    gap: theme.resolve("spacing_xs"),
                  }}
                >
                  {getResubmitApprovalButton(original, is_disabled)}
                  {getCloneButton(original, is_disabled)}
                  {getDeleteButton(original, is_disabled)}
                  {getEditButton(original, is_disabled)}
                </IconGroup>
              );
            },
          });
        }
        return acc;
      }, []) || [];

    if (!shouldShowBarentzApiStatusFeatures) {
      return baseColumns;
    }

    const apiStatusColumn = {
      header: () => t("API Status"),
      accessorKey: "__api_status",
      enableSorting: false,
      meta: {
        cellPadding: "0 10px",
      },
      cell: ({ row: { original } }: { row: { original: IRow } }) => {
        const rowSkuRef =
          skuIdColId != null
            ? (() => {
                const raw = original[skuIdColId];
                if (raw == null) return original.uuid;
                if (
                  typeof raw === "object" &&
                  "value" in raw &&
                  raw.value != null
                ) {
                  return String(raw.value);
                }
                return String(raw);
              })()
            : original.uuid;
        const rowBatches = barentzApiBatches.filter(
          (batch) =>
            batch.sku_number === rowSkuRef || batch.sku_number === original.uuid
        );
        // get latest status
        const barentzApiBatch =
          rowBatches.length > 0
            ? [...rowBatches].sort(
                (a, b) =>
                  new Date(b.push_finished || 0).getTime() -
                  new Date(a.push_finished || 0).getTime()
              )[0]
            : null;
        const { color: statusColor, text: statusText } =
          getBarentzApiStatusConfig(barentzApiBatch?.status, t);
        const statusShowsTimestamp = [
          "success",
          "failed",
          "out_of_sync",
        ].includes(barentzApiBatch?.status ?? "");
        const timestamp =
          statusShowsTimestamp &&
          (barentzApiBatch?.push_finished || barentzApiBatch?.push_started)
            ? moment(
                barentzApiBatch.push_finished || barentzApiBatch.push_started
              ).format("MMM DD YYYY [at] hh:mm A")
            : "";
        return (
          <APIStatus
            text={statusText}
            color={statusColor}
            timestamp={timestamp}
            id={`api-status-${original.uuid}`}
            onInfoClick={
              barentzApiBatch?.status === "failed"
                ? () => {
                    setSelectedAPIStatusBatch(barentzApiBatch ?? null);
                    setShowAPIStatusModal(true);
                  }
                : undefined
            }
          />
        );
      },
    };

    // put API status at last before action buttons column
    const lastColumn = baseColumns[baseColumns.length - 1];
    const isActionsColumn = lastColumn?.accessorKey === " ";
    if (isActionsColumn && baseColumns.length > 0) {
      const fieldColumns = baseColumns.slice(0, -1);
      return [...fieldColumns, apiStatusColumn, lastColumn];
    }
    return [...baseColumns, apiStatusColumn];
  }, [
    adminPath,
    barentzApiBatches,
    cloneItem,
    collectionSchema.name,
    collectionSchema.object_type,
    columns,
    enable_approval_workflow,
    enable_change_requests,
    handleResubmitApproval,
    hasPermission,
    isEditable,
    isPortfolio,
    isSubmitting,
    isSubmittingApplicability,
    notifyError,
    notifySuccess,
    onDeleteRow,
    productStatus,
    productTypePermissionSuffix,
    roleIsSellerAdmin,
    rowInEdit,
    searchQuery,
    shouldShowBarentzApiStatusFeatures,
    showMandatoryAsterisk,
    showSlideOut,
    skuIdColId,
    skuSourceColId,
    skuStatusColId,
    storePath,
    t,
    theme,
    uiRules,
    user,
  ]);

  const changeRequestContext = useMemo(
    () => ({
      variant: "collection" as const,
      collectionData: {
        collection: collectionSchema,
        entityName: "Product",
        rowId: changeRequestRow,
      },
    }),
    [changeRequestRow, collectionSchema]
  );

  const onSubmitChangeRequest = async (formValues: ChangeRequestFormValues) => {
    setIsSubmitting(true);
    try {
      await submitChangeRequest({
        tenant_id,
        entity: { id: productID },
        isProduct: true,
        context: changeRequestContext,
        formValues,
        onSuccess: () => setshowChangeRequestSlideOut(false),
      });
      notifySuccess(t("Change request submitted successfully."));
    } catch (error) {
      console.error(error);
      notifyError(t("Failed to submit change request."));
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    setTableData(() => {
      const dataFromDB = (collectionSchema?.value_rows ?? []).reduce<IRow[]>(
        (acc, row) => {
          const temp = row.values.reduce<IRow>(
            (attributeValueAcc, attrValue) => {
              const is_sku_source = attrValue.attribute_id === skuSourceColId;
              return {
                ...attributeValueAcc,
                [attrValue.attribute_id]: getAttributeValue(
                  attrValue,
                  attributeValueAcc,
                  is_sku_source
                ),
                uuid: row.row_id,
                edit: false,
              };
            },
            {
              uuid: row.row_id,
              edit: false,
              row_editable:
                pimProductType === "variant"
                  ? row.is_editable && !row.is_master
                  : row.is_editable,
              id: `row-${row.row_id}`,
            }
          );
          return row.values.length > 0 ? [...acc, temp] : acc;
        },
        []
      );
      return dataFromDB;
    });
    setIsSubmitting(false);
  }, [collectionSchema?.value_rows, columns, pimProductType, skuSourceColId]);

  useEffect(() => {
    const skuIdDisplayName = columns?.find(
      (col) => col.object_type === "sku_id"
    )?.display_name;
    setSkuIdDisplayName(skuIdDisplayName || "SKU ID");
  }, [columns]);

  // Get the current row being edited
  const currentEditRow = useMemo(() => {
    if (!rowInEdit) return null;
    return tableData.find((row) => row.uuid === rowInEdit) ?? null;
  }, [rowInEdit, tableData]);

  if (!columns || columns.length === 0) {
    return <></>;
  }

  return (
    <>
      <AccordionCard
        title={title}
        actionChildren={() => (
          <APIResyncButton
            barentzApiBatches={barentzApiBatches}
            storefrontId={storefront_id}
            onResyncComplete={fetchProductData}
            enabled={shouldShowBarentzApiStatusFeatures}
          />
        )}
        expanded={true}
        hideHeaderBorder={true}
      >
        {(() => {
          const filterableColumnDefs = filterableColumns.filter(
            (col) =>
              (activeFilters[col.id]?.length ?? 0) > 0 ||
              (columnFilterOptions[col.id]?.length ?? 0) > 0 ||
              // AP-1016 (Advanced Filters, Part 1): numeric columns are
              // always filterable via operator + value, independent of
              // which discrete display strings happen to exist in the data.
              col.input_type === "numeric"
          );
          const hasActiveFilters = Object.values(activeFilters).some(
            (chips) => chips.length > 0
          );
          const filtersSlot =
            filterableColumnDefs.length > 0 ? (
              <>
                {filterableColumnDefs.map((col) => {
                  const label = col.display_name
                    ? t([col.display_name])
                    : col.name;
                  if (col.input_type === "numeric") {
                    return (
                      <NumericFilterButton
                        key={col.id}
                        filterType={col.id}
                        label={label}
                        operators={NUMERIC_FILTER_OPERATORS}
                        preselectedFilters={activeFilters[col.id] ?? []}
                        setter={handleFilterChange}
                        usePortal
                      />
                    );
                  }
                  return (
                    <FilterBy
                      key={col.id}
                      filter={col.id}
                      label={label}
                      header={label}
                      options={columnFilterOptions[col.id] ?? []}
                      preselectedFilters={activeFilters[col.id] ?? []}
                      setter={handleFilterChange}
                      size="small"
                      usePortal
                    />
                  );
                })}
              </>
            ) : undefined;

          return (
            <TablePageLayout
              searchQuery={
                searchQuery && tableData.length > 0 ? searchQuery : undefined
              }
              searchPlaceholder={t("Search...")}
              onSearchChange={
                collectionSchema.enable_collection_search &&
                tableData.length > 0
                  ? (e) => setSearchQuery(e.target.value)
                  : undefined
              }
              onSearchClear={() => setSearchQuery("")}
              totalCount={
                filteredTableData.length > 0 && tableData.length > 0
                  ? tableData.length
                  : undefined
              }
              searchFilterCount={
                filteredTableData.length > 0
                  ? filteredTableData.length
                  : undefined
              }
              totalCountLabel={
                filteredTableData.length > 0
                  ? tableData.length === 1
                    ? t("item")
                    : t("items")
                  : undefined
              }
              filtersSlot={
                collectionSchema.enable_collection_filter &&
                tableData.length > 0
                  ? filtersSlot
                  : undefined
              }
              showFilters={
                collectionSchema.enable_collection_filter &&
                tableData.length > 0
                  ? !!filtersSlot
                  : false
              }
              showClearAll={hasActiveFilters}
              onClearAll={handleClearAllFilters}
              clearAllLabel={t("Clear Filters")}
              containerStyle={{ marginTop: 0 }}
              tableSlot={
                <TableContainer canStickLastColumn={canStickLastColumn}>
                  <ScrollTableWrapper
                    maxHeight="383px"
                    ref={collectionTableRef}
                  >
                    {(isSubmitting || isSubmittingApplicability) && (
                      <TableStickyLoader />
                    )}
                    <Table
                      columns={tableColumns}
                      data={filteredTableData}
                      lastChildleftAlign
                      isLoading={false}
                      enableDefaultSort={true}
                      error={undefined}
                      highlightLinks
                      Placeholder={
                        !isPortfolio
                          ? TablePlaceholder({})
                          : NoEditPlaceholder(t)
                      }
                      rowHover={(data) => rowHover(data, rowHoverProcessor)}
                    />
                  </ScrollTableWrapper>
                </TableContainer>
              }
            />
          );
        })()}
        {isPortfolio ||
        productStatus === "archived" ||
        !isEditable ||
        columns.every((col) => col.is_editable === false) ? (
          <></>
        ) : (
          !showSlideOut && (
            <div>
              <TertiaryButtonMediumV2
                style={{
                  width: "fit-content",
                }}
                onClick={addItem}
                disabled={
                  !hasPermission("modify_products") ||
                  !hasPermission(`modify_${productTypePermissionSuffix}`) ||
                  !isEditable
                }
                datafor={"modify-products"}
                datatip={
                  !hasPermission("modify_products") ||
                  !hasPermission(`modify_${productTypePermissionSuffix}`)
                    ? t("You don't have permission to modify this product")
                    : !isEditable
                    ? isLockedForEdit && hasPermission("modify_products")
                      ? t(
                          "This product is locked for edit and cannot be edited"
                        )
                      : t("You don't have permission to modify this product")
                    : ""
                }
              >
                <PlusIcon />
                {t("Add")}
              </TertiaryButtonMediumV2>
              <ReactTooltip id="modify-products" />
            </div>
          )
        )}
        <ConfirmDialog
          show={showConfirmDialog}
          closeDialog={() => {
            setShowConfirmDialog(false);
            setPendingAction(null);
          }}
          confirmMessage={
            pendingAction?.newStatus
              ? t(
                  "Are you sure you want to mark this column as not applicable?"
                )
              : t("Are you sure you want to mark this column as applicable?")
          }
          handleConfirm={handleConfirmApplicability}
        />
        <ErrorDialog
          show={showAPIStatusModal}
          onClose={() => {
            setShowAPIStatusModal(false);
            setSelectedAPIStatusBatch(null);
          }}
          title={t("API Sync Error")}
          subtitle={t("{{skuIdDisplayName}}: {{skuId}}", {
            skuIdDisplayName,
            skuId: selectedAPIStatusBatch?.sku_number ?? "",
          })}
          primaryButtonLabel={t("Close")}
          icon={
            <ErrorIcon width={40} height={40} fill={theme.errorIconColor} />
          }
        >
          <BarentzAPIErrorMessage
            raw={selectedAPIStatusBatch?.response_error_message}
          />
        </ErrorDialog>
        {documentMoreDocs && (
          <DocumentMoreModal
            docs={documentMoreDocs.docs}
            attributeName={documentMoreDocs.attributeName}
            onClose={() => setDocumentMoreDocs(null)}
          />
        )}
        {objectReferenceMore && (
          <ObjectReferenceMoreModal
            references={objectReferenceMore.references}
            attributeName={objectReferenceMore.attributeName}
            targetObjectType={objectReferenceMore.targetObjectType}
            onClose={() => setObjectReferenceMore(null)}
            linkOptions={{ isPortfolio, storePath, adminPath }}
            accessOptions={{
              isPortfolio,
              user,
              roleIsSellerAdmin,
              hasPermission,
            }}
          />
        )}
        {productReferenceMore && (
          <ProductCardsMoreModal
            references={productReferenceMore.references}
            attributeName={productReferenceMore.attributeName}
            onClose={() => setProductReferenceMore(null)}
            storePath={storePath}
            adminPath={adminPath}
            accessOptions={{
              isPortfolio,
              user,
              roleIsSellerAdmin,
              hasPermission,
            }}
          />
        )}
        {showDuplicateSkuModal && (
          <DuplicateSKUModal
            matchingSkuId={matchingSkuId}
            onClose={() => {
              setShowDuplicateSkuModal(false);
              setMatchingSkuId(null);
            }}
            skuIdDisplayName={skuIdDisplayName}
          />
        )}
        {showResubmitModal && productID && (
          <ResubmitConfirmationModal
            productId={productID}
            skuId={resubmitSkuId ?? ""}
            onClose={() => {
              setShowResubmitModal(false);
              setResubmitSkuId(null);
            }}
            onConfirm={async () => {
              setShowResubmitModal(false);
              setResubmitSkuId(null);
              if (fetchProductData) {
                await fetchProductData();
              }
              await triggerUpdate();
            }}
          />
        )}
        {showSlideOut && (
          <CollectionSchemaSlideOut
            show={showSlideOut}
            onClose={closeSlideOut}
            onSubmit={onSubmit}
            columns={columns}
            row={currentEditRow}
            rowUuid={rowInEdit}
            isNewRow={isNewRow}
            isClone={isClone}
            collectionSchema={collectionSchema}
            tenant_id={tenant_id}
            storefront_id={storefront_id}
            isSubmitting={isSubmitting}
            enable_reference_entities={enable_reference_entities}
            has_reference_entity_mapping={
              collectionSchema.has_reference_entity_mapping
            }
            enable_generic_sku={enable_generic_sku}
            generic_sku_packaging_type_name={generic_sku_packaging_type_name}
            packagingTypeColId={packagingTypeColId}
            skuSourceColId={skuSourceColId}
            allRows={tableData}
            multiSelectRefs={multiSelectRefs}
            excludeProfileId={productID}
            uiRules={uiRules}
          />
        )}
        {showChangeRequestSlideOut && (
          <ChangeRequestFormModal
            open={showChangeRequestSlideOut}
            onClose={() => {
              setshowChangeRequestSlideOut(false);
              setChangeRequestRow(undefined);
            }}
            assigneePermissions={[`modify_${productTypePermissionSuffix}`]}
            variant={changeRequestContext.variant}
            collectionData={changeRequestContext.collectionData}
            onSubmit={onSubmitChangeRequest}
            submitting={isSubmitting}
            assignee={productOwner}
          />
        )}
      </AccordionCard>
      {showDuplicateRowError && (
        <DuplicateItemModal
          show={showDuplicateRowError}
          onClose={() => setShowDuplicateRowError(false)}
        />
      )}
    </>
  );
};
